import { useId, type ReactNode } from "react";

import { Label } from "@sirdab/ui";

export interface FieldRenderProps {
  id: string;
  "aria-describedby": string | undefined;
  "aria-invalid": true | undefined;
}

export interface FieldProps {
  /** Already-localised. Callers pass `t(key)` — never a literal (apps/portal/AGENTS.md). */
  label: string;
  /** Optional supporting line. Also already-localised. */
  hint?: string | undefined;
  /** Already-localised error text; usually the active side of an API `reasonText`. */
  error?: string | undefined;
  /**
   * Kept for the callers' intent, rendered as nothing: required is the majority on every
   * portal form, so it goes unmarked (Label doctrine, never an asterisk).
   */
  required?: boolean;
  children: (props: FieldRenderProps) => ReactNode;
}

/**
 * Label + control + hint/error, wired for screen readers. Render-prop rather than a wrapped
 * `<Input>` so a step can put a radio group, a file picker or a quantity stepper inside the
 * same labelling contract. The recipe is the house one: label, field, helper, 6px apart;
 * an error is destructive TEXT under the field and never colours the border.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: FieldProps): React.JSX.Element {
  const id = useId();
  const hintId = hint !== undefined ? `${id}-hint` : undefined;
  const errorId = error !== undefined ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error !== undefined ? true : undefined,
      })}
      {hint !== undefined ? (
        <p id={hintId} className="text-caption-quiet text-subtle">
          {hint}
        </p>
      ) : null}
      {error !== undefined ? (
        <p id={errorId} role="alert" className="text-caption text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
